import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import {
  PurgeLogEntry,
  PurgePreview,
  PurgeUserPayload,
  PurgeUserResult,
  UserLookupResult,
} from "../../types/superadminGdpr";

interface SuperadminGdprState {
  lookup: UserLookupResult | null;
  lookupStatus: "idle" | "loading" | "failed" | "notFound";
  lookupError: string | null;
  preview: PurgePreview | null;
  previewStatus: "idle" | "loading" | "failed";
  previewError: string | null;
  purgeStatus: "idle" | "loading" | "failed" | "succeeded";
  purgeError: string | null;
  lastResult: PurgeUserResult | null;
  logs: PurgeLogEntry[];
  logsStatus: "idle" | "loading" | "failed";
  logsError: string | null;
}

const initialState: SuperadminGdprState = {
  lookup: null,
  lookupStatus: "idle",
  lookupError: null,
  preview: null,
  previewStatus: "idle",
  previewError: null,
  purgeStatus: "idle",
  purgeError: null,
  lastResult: null,
  logs: [],
  logsStatus: "idle",
  logsError: null,
};

export const fetchPurgeLogs = createAsyncThunk<
  PurgeLogEntry[],
  { limit?: number; offset?: number } | undefined,
  { rejectValue: string }
>("superadminGdpr/fetchPurgeLogs", async (params, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/superadmin/gdpr/purge-logs`, {
      params: { limit: params?.limit ?? 50, offset: params?.offset ?? 0 },
    });
    return response.data as PurgeLogEntry[];
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "Lokin haku epäonnistui"
    );
  }
});

export const lookupUser = createAsyncThunk<
  UserLookupResult | null,
  string,
  { rejectValue: string }
>("superadminGdpr/lookupUser", async (email, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/superadmin/users/lookup`, {
      params: { email },
    });
    return response.data as UserLookupResult | null;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "Käyttäjähaku epäonnistui"
    );
  }
});

export const fetchPurgePreview = createAsyncThunk<
  PurgePreview,
  number,
  { rejectValue: string }
>("superadminGdpr/fetchPreview", async (userId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(
      `/superadmin/users/${userId}/purge-preview`
    );
    return response.data as PurgePreview;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "Esikatselun haku epäonnistui"
    );
  }
});

export const purgeUser = createAsyncThunk<
  PurgeUserResult,
  { userId: number; payload: PurgeUserPayload },
  { rejectValue: string }
>(
  "superadminGdpr/purgeUser",
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/superadmin/users/${userId}/purge`,
        payload
      );
      return response.data as PurgeUserResult;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.response?.data ||
          err.message ||
          "Käyttäjän poisto epäonnistui"
      );
    }
  }
);

const superadminGdprSlice = createSlice({
  name: "superadminGdpr",
  initialState,
  reducers: {
    resetGdprState(state) {
      state.preview = null;
      state.previewStatus = "idle";
      state.previewError = null;
      state.purgeStatus = "idle";
      state.purgeError = null;
      state.lastResult = null;
    },
    resetLookup(state) {
      state.lookup = null;
      state.lookupStatus = "idle";
      state.lookupError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurgeLogs.pending, (state) => {
        state.logsStatus = "loading";
        state.logsError = null;
      })
      .addCase(fetchPurgeLogs.fulfilled, (state, action) => {
        state.logsStatus = "idle";
        state.logs = action.payload;
      })
      .addCase(fetchPurgeLogs.rejected, (state, action) => {
        state.logsStatus = "failed";
        state.logsError =
          (action.payload as string) ||
          action.error.message ||
          "Lokin haku epäonnistui";
      })
      .addCase(lookupUser.pending, (state) => {
        state.lookupStatus = "loading";
        state.lookupError = null;
        state.lookup = null;
      })
      .addCase(lookupUser.fulfilled, (state, action) => {
        state.lookup = action.payload;
        state.lookupStatus = action.payload ? "idle" : "notFound";
      })
      .addCase(lookupUser.rejected, (state, action) => {
        state.lookupStatus = "failed";
        state.lookupError =
          (action.payload as string) ||
          action.error.message ||
          "Käyttäjähaku epäonnistui";
      })
      .addCase(fetchPurgePreview.pending, (state) => {
        state.previewStatus = "loading";
        state.previewError = null;
        state.preview = null;
      })
      .addCase(fetchPurgePreview.fulfilled, (state, action) => {
        state.previewStatus = "idle";
        state.preview = action.payload;
      })
      .addCase(fetchPurgePreview.rejected, (state, action) => {
        state.previewStatus = "failed";
        state.previewError =
          (action.payload as string) ||
          action.error.message ||
          "Esikatselun haku epäonnistui";
      })
      .addCase(purgeUser.pending, (state) => {
        state.purgeStatus = "loading";
        state.purgeError = null;
      })
      .addCase(purgeUser.fulfilled, (state, action) => {
        state.purgeStatus = "succeeded";
        state.lastResult = action.payload;
      })
      .addCase(purgeUser.rejected, (state, action) => {
        state.purgeStatus = "failed";
        state.purgeError =
          (action.payload as string) ||
          action.error.message ||
          "Käyttäjän poisto epäonnistui";
      });
  },
});

export const { resetGdprState, resetLookup } = superadminGdprSlice.actions;

export default superadminGdprSlice.reducer;
